import {
  Box,
  Checkbox,
  Link,
  List,
  ListItemButton,
  Switch,
  Tooltip,
  Typography,
} from "@mui/joy";
import { WorkSession } from "entities/workSession";
import { useMissionControlModeTint } from "hooks/Layout/v2";
import { useDispatch, useSelector } from "react-redux";
import {
  missionControlModeSelector,
  missionControlViewModeSelector,
  setActionInitiator,
  setFormOpen,
  setSelectedVenueID,
  setSelectedWorkSessionID,
} from "reducers/rhapsody";
import {
  chairIDsHoveredSelector,
  layoutDebugSelector,
  layoutUtilsSelector,
  selectionSelector,
  setSelectedWorkSessionIDs,
} from "reducers/v2/missionControl";
import { EditButton, SoloButton, oneTrue, useSoloWorkSession } from "./utils";
import { typeOptions } from "constants/workSessionTypeOptions";
import { useLocalStorage } from "@uidotdev/usehooks";

/**
 *
 * @returns {ReactElement} WorkSessionsSelect page
 */
export function WorkSessionsSelect({ hideEdit }: { hideEdit?: boolean }) {
  const utils = useSelector(layoutUtilsSelector);
  const handleSoloWorkSession = useSoloWorkSession();
  const dispatch = useDispatch();
  const { selectedWorkSessionIDs } = useSelector(selectionSelector);
  const { workSessions } = utils;
  const missionControlViewMode = useSelector(missionControlViewModeSelector);
  const hasOneWorkSessionSelected = oneTrue(selectedWorkSessionIDs);
  const [option] = useLocalStorage("workSessionSortOption", "chronologically");
  const handleSelectWorkSession = (id: number) => {
    const currentValue = selectedWorkSessionIDs[id] ?? false;
    const nextValue = !currentValue;
    dispatch(setActionInitiator("workSession"));

    if (!(hasOneWorkSessionSelected && currentValue)) {
      // dispatch(layoutUnselectAll());
      dispatch(
        setSelectedWorkSessionIDs({
          ...selectedWorkSessionIDs,
          [id]: nextValue,
        })
      );
    }
  };

  const options = workSessions
    .reduce((a, v) => {
      if (!a.includes(v.typeOption())) a.push(v.typeOption());
      return a;
    }, [])
    .sort((a, b) => a.order - b.order);

  const getLineItem = (w: WorkSession) => {
    return (
      <WorkSessionListItemButton
        toggle
        typeOptionIcon={option === "chronologically"}
        onSolo={() => handleSoloWorkSession(w.id)}
        onClick={() => handleSelectWorkSession(w.id)}
        onEdit={
          !hideEdit
            ? () => {
                dispatch(setSelectedWorkSessionID(w.id));
                dispatch(
                  setFormOpen({ formID: "workSessionEdit", isOpen: true })
                );
              }
            : undefined
        }
        disableCheck={hasOneWorkSessionSelected && selectedWorkSessionIDs[w.id]}
        disabled={missionControlViewMode === "workSessions"}
        key={w.id}
        selected={selectedWorkSessionIDs[w.id]}
        workSession={w}
      />
    );
  };

  return (
    <List size="sm" sx={{ p: 0 }}>
      {option === "chronologically"
        ? workSessions.map((w) => getLineItem(w))
        : []}
      {option === "grouped"
        ? options.map((o) => {
            const ws = workSessions.filter(
              (w) => w.sessionTypeID === o.sessionTypeID
            );
            return (
              <>
                <Typography
                  level="body3"
                  color="neutral"
                  variant="soft"
                  sx={{
                    pl: 2,
                    pr: 2,
                    borderRadius: 4,
                    background: "#eeeeee",
                  }}
                  key={o.id}
                  startDecorator={<i className={o.icon} />}
                >
                  {o.title}
                </Typography>
                {ws.map((w) => getLineItem(w))}
              </>
            );
          })
        : []}
    </List>
  );
}

function WorkSessionListItemButton({
  selected,
  workSession,
  disableCheck,
  onClick,
  onEdit,
  onSolo,
  disabled,
  toggle,
  typeOptionIcon,
}: {
  selected: boolean;
  workSession: WorkSession;
  disableCheck?: boolean;
  typeOptionIcon?: boolean;
  onClick: () => void;
  onEdit?: () => void;
  onSolo: () => void;
  disabled?: boolean;
  toggle?: boolean;
}) {
  const debug = useSelector(layoutDebugSelector);
  const chairIDsHovered = useSelector(chairIDsHoveredSelector);
  const missionControlModeTint = useMissionControlModeTint();
  const missionControlNode = useSelector(missionControlModeSelector);
  const utils = useSelector(layoutUtilsSelector);
  const dispatch = useDispatch();
  let CheckComponent = Checkbox;
  if (toggle) CheckComponent = Switch;

  let variant: "soft" | undefined;
  chairIDsHovered?.forEach((c) => {
    const _chair = utils.chairsMap[c];
    if (_chair?.workSessionIDs.includes(workSession.id)) {
      variant = "soft";
      return;
    }
  });

  const typeOption = typeOptions.find(
    (e) => e.sessionTypeID === workSession.sessionTypeID
  );

  return (
    <ListItemButton
      sx={{
        gap: 1,
        justifyContent: "space-between",
        cursor: disableCheck || disabled ? "not-allowed" : undefined,
        transition: "background .2s",
        opacity: workSession.archived ? 0.58 : 1,
      }}
      selected={selected}
      onClick={() => {
        if (!disableCheck && !disabled) {
          onClick();
        }
      }}
      variant={variant}
      onDoubleClick={onEdit}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
        }}
      >
        <CheckComponent
          disabled={disabled}
          variant={disableCheck ? "soft" : undefined}
          color={toggle ? undefined : "primary"}
          size="sm"
          sx={{ opacity: disabled ? 0.4 : 1 }}
          checked={selected ?? false}
        />
        <Box>
          <Typography
            endDecorator={
              <>
                <Tooltip
                  arrow
                  size="sm"
                  variant="outlined"
                  title={workSession.fromNowTooltip()}
                >
                  <Typography
                    color={workSession.fromNowColor()}
                    variant={"soft"}
                    noWrap
                    level="body5"
                  >
                    {workSession.completed() ? (
                      <i className="fa-solid fa-check"></i>
                    ) : undefined}
                    {workSession.isTBD() ? (
                      <i className="fa-solid fa-question"></i>
                    ) : undefined}
                    {!workSession.isTBD() && !workSession.completed()
                      ? workSession.fromNow()
                      : undefined}
                  </Typography>
                </Tooltip>
                {debug ? (
                  <Typography level="body5">{workSession.id}</Typography>
                ) : undefined}
              </>
            }
            startDecorator={
              workSession.archived ? (
                <i className="fa-solid fa-box-archive"></i>
              ) : typeOptionIcon ? (
                <Tooltip title={typeOption?.title} size="sm" variant="outlined">
                  <i className={typeOption?.icon}></i>
                </Tooltip>
              ) : undefined
            }
            sx={{
              fontWeight: "normal",
              color: selected ? missionControlModeTint : undefined,
            }}
            level="body2"
          >
            {workSession.title}
          </Typography>
          <Typography
            sx={{
              fontWeight: "normal",
              color: selected ? missionControlModeTint : undefined,
            }}
            level="body3"
          >
            {workSession.formatDateRange()}
            <Link
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (workSession.venueID > 0)
                  dispatch(setSelectedVenueID(workSession.venueID));
              }}
              sx={{ pl: 0.5 }}
              level="body3"
              color="neutral"
            >
              {workSession.venueID > 0
                ? `@${workSession.studioName}`
                : "Venue to be determined"}
            </Link>
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: 0.5 }}>
        {missionControlNode === "edit" && onEdit ? (
          <EditButton onClick={onEdit} />
        ) : (
          []
        )}

        {onSolo ? (
          <Box
            sx={{
              opacity: disabled ? 0.4 : 1,
              cursor: disableCheck || disabled ? "not-allowed" : undefined,
              pointerEvents: disableCheck || disabled ? "none" : undefined,
            }}
          >
            <SoloButton onClick={!disabled && onSolo} />
          </Box>
        ) : (
          []
        )}
      </Box>
    </ListItemButton>
  );
}
